'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailsTab } from '@repo/components';
import { LanguageCode } from '@repo/shared/domain';
import { EventEmitter, guardedClient as request } from '@repo/shared/helpers';
import { getProfile } from 'src/api/admin/users';
import { getForm } from 'src/api/admin/forms';

export const Route = createFileRoute('/_admin/users/$userId/details')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			language: (search.language as string) || undefined,
		};
	},
	loaderDeps: ({ search: { language } }) => ({ language }),
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const profile = await getProfile(userId);

		const form = profile?.userForm?.slug ? await getForm(profile.userForm.slug, deps.language) : undefined;

		return { profile, form };
	},
	component: DetailsTabPage,
});

function DetailsTabPage() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { profile: initialProfile, form: initialForm } = Route.useLoaderData();
	const { language: searchParamsLanguage } = Route.useSearch();

	const { data: profile, refetch: refetchProfile } = useQuery({
		queryKey: ['user-profile', userId],
		queryFn: () => getProfile(userId),
		initialData: initialProfile,
	});

	const availableLanguages = [...(profile?.userForm?.availableLanguages || [])];
	const defaultLanguage = profile?.userForm?.language ?? 'en';
	const languages = [...new Set([...availableLanguages, defaultLanguage])] as LanguageCode[];
	const language = languages.find((lang) => lang === searchParamsLanguage) || 'en';

	const { data: form } = useQuery({
		queryKey: ['user-form', profile?.userForm?.slug, language],
		queryFn: () => (profile?.userForm?.slug ? getForm(profile.userForm.slug, language) : undefined),
		initialData: initialForm,
		enabled: !!profile?.userForm?.slug,
	});

	const handleDetailsUpdate = async (userData: Record<string, unknown>) => {
		await request
			.put(`/api/users/${userId}/user-data`, userData)
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Changes saved',
				});
			})
			.catch(() => {
				EventEmitter.emit('toast', { type: 'error', message: 'Error' });
			});
		refetchProfile();
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
	};

	const handleLanguageChange = (lang: string) => {
		navigate({
			to: '/users/$userId/details',
			params: { userId },
			search: { language: lang },
		});
	};

	if (!profile) {
		return null;
	}

	return (
		<DetailsTab
			data={profile.user.userData}
			form={form}
			onSave={handleDetailsUpdate}
			language={language}
			onLanguageChange={handleLanguageChange}
			availableLanguages={languages}
		/>
	);
}
